import React from 'react';
import { Box, TextField, Grid, Tooltip, IconButton, useTheme } from '@mui/material';
import type { UseFormReturn } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace';
import type { I18nScenarioEditorNs, I18nPersonasNs } from '@/i18n/dictionaries/interfaces';
import type { RoleDto } from '@/api-gen/api.schemas';
import { AutoAwesomeOutlined } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import AIRewriteDialog from '@/app/components/PersonasManager/PersonaDetailDialog/AIRewrite/AIRewriteDialog';
import { useRoleAIRewrite } from './useRoleAIRewrite';

const ROLE_NAME_MAX = 100;
const ROLE_DESCRIPTION_MAX = 2000;

export type RoleFormData = {
  name: string;
  description?: string | null;
};

interface RoleFormProps {
  role?: RoleDto | null;
  readOnly?: boolean;
  formId?: string;
  onSubmit?: (data: RoleFormData) => void;
  // Expose the form instance if parent needs it
  onFormInit?: (form: UseFormReturn<RoleFormData>) => void;
}

const RoleForm: React.FC<RoleFormProps> = ({ role, readOnly = false, formId = 'role-form', onSubmit, onFormInit }) => {
  const [translate] = useTranslation([I18nNamespace.ScenarioEditor]);
  const [translatePersonas] = useTranslation([I18nNamespace.Personas]);
  const theme = useTheme();

  const schema = React.useMemo(
    () =>
      z.object({
        name: z
          .string()
          .min(
            1,
            translate(
              nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.roleNameRequired)
            )
          )
          .max(
            ROLE_NAME_MAX,
            translate(
              nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.roleNameMaxLength)
            )
          ),
        description: z
          .string()
          .max(
            ROLE_DESCRIPTION_MAX,
            translate(
              nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.descriptionMaxLength)
            )
          )
          .optional()
          .nullable()
      }),
    [translate]
  );

  const form = useForm<RoleFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: role?.name || '',
      description: role?.description || ''
    }
  });
  const watchedName = form.watch('name');
  const aiRewrite = useRoleAIRewrite((rewrittenText: string) => {
    form.setValue('description', rewrittenText, { shouldValidate: true, shouldDirty: true });
  });

  React.useEffect(() => {
    if (onFormInit) {
      onFormInit(form);
    }
  }, [form, onFormInit]);

  // Update default values when role changes
  React.useEffect(() => {
    if (role) {
      form.reset({
        name: role.name || '',
        description: role.description || ''
      });
    }
  }, [role, form]);

  const handleSubmit = (data: RoleFormData) => {
    if (onSubmit) {
      onSubmit(data);
    }
  };

  return (
    <form id={formId} onSubmit={form.handleSubmit(handleSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Controller
            control={form.control}
            name="name"
            render={({ field }) => (
              <TextField
                {...field}
                label={translate(
                  nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.roleName)
                )}
                fullWidth
                required
                disabled={readOnly}
                error={!!form.formState.errors.name}
                helperText={form.formState.errors.name?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            control={form.control}
            name="description"
            render={({ field }) => (
              <Box sx={{ position: 'relative' }}>
                <TextField
                  {...field}
                  value={field.value || ''}
                  label={translate(
                    nameof.full<I18nScenarioEditorNs>(
                      (n) => n.scenarioPersonasSettings.roleManagement.descriptionOptional
                    )
                  )}
                  placeholder={translate(
                    nameof.full<I18nScenarioEditorNs>(
                      (n) => n.scenarioPersonasSettings.roleManagement.descriptionOptional
                    )
                  )}
                  fullWidth
                  multiline
                  minRows={8}
                  maxRows={20}
                  disabled={readOnly}
                  error={!!form.formState.errors.description}
                  helperText={form.formState.errors.description?.message}
                />
                <Tooltip
                  title={
                    readOnly
                      ? translatePersonas(nameof.full<I18nPersonasNs>((n) => n.validation.readOnlyTooltip))
                      : translatePersonas(nameof.full<I18nPersonasNs>((n) => n.aiRewrite.buttonTooltip))
                  }
                >
                  <IconButton
                    size="small"
                    onClick={() =>
                      aiRewrite.openDialog(field.value || '', watchedName, role?.languageCode || undefined)
                    }
                    aria-label={translatePersonas(nameof.full<I18nPersonasNs>((n) => n.aiRewrite.buttonAriaLabel))}
                    disabled={aiRewrite.isLoading || readOnly}
                    sx={{
                      position: 'absolute',
                      bottom: theme.spacing(1),
                      right: theme.spacing(1),
                      backgroundColor: alpha(theme.palette.background.paper, 0.8),
                      '&:hover': {
                        backgroundColor: theme.palette.background.paper
                      },
                      zIndex: 1
                    }}
                  >
                    <AutoAwesomeOutlined fontSize="medium" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          />
        </Grid>
      </Grid>

      <AIRewriteDialog
        isOpen={aiRewrite.isOpen}
        currentStep={aiRewrite.currentStep}
        originalDescription={aiRewrite.originalDescription}
        customInstructions={aiRewrite.customInstructions}
        rewrittenDescription={aiRewrite.rewrittenDescription}
        isLoading={aiRewrite.isLoading}
        error={aiRewrite.error}
        onInstructionsChange={aiRewrite.setCustomInstructions}
        onSubmit={aiRewrite.submitRewrite}
        onConfirm={aiRewrite.confirmRewrite}
        onBack={aiRewrite.goBackToInstructions}
        onClose={aiRewrite.closeDialog}
        onClearError={aiRewrite.clearError}
      />
    </form>
  );
};

export default RoleForm;
